// src/components/pages/admin-dash/AdminFinancialReport.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import AdminStatCard from "./AdminStatCard";
import AdminTable from "./AdminTable";

/**
 * Admin report — revenue by plan, refunds, cancellation rate
 * (GET /admin/reports/financial). Roadmap item 3.6.
 */
const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : "$0";

const fmtPct = (n) => (typeof n === "number" ? `${n.toFixed(1)}%` : "—");

const todayIso = () => new Date().toISOString().split("T")[0];
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
};

export default function AdminFinancialReport() {
  const [startDate, setStartDate] = useState(daysAgoIso(30));
  const [endDate, setEndDate] = useState(todayIso());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/admin/reports/financial", { params: { startDate, endDate } });
      setReport(res.data);
    } catch {
      setError("Failed to load financial report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []); // eslint-disable-line

  const subColumns = [
    { key: "plan", label: "Plan", sort: true, value: (r) => r.plan || "" },
    { key: "count", label: "Transactions", sort: true, align: "right",
      render: (r) => <span className="tabular-nums">{r.count}</span> },
    { key: "totalUsd", label: "Revenue", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium" style={{ color: "var(--gold)" }}>{fmtUsd(r.totalUsd)}</span> },
  ];

  const creditColumns = [
    { key: "creditPlan", label: "Credit plan", sort: true, value: (r) => r.creditPlan || "" },
    { key: "count", label: "Purchases", sort: true, align: "right",
      render: (r) => <span className="tabular-nums">{r.count}</span> },
    { key: "totalUsd", label: "Revenue", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium" style={{ color: "var(--gold)" }}>{fmtUsd(r.totalUsd)}</span> },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Financial report
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Revenue by plan, refunds, and subscription churn over a date range.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <Field label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!mb-0" />
          <Field label="To" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!mb-0" />
          <div className="flex items-end">
            <Button icon="search" size="sm" onClick={fetchReport} loading={loading}>Search</Button>
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <AdminStatCard label="Total revenue" value={fmtUsd(report?.totalRevenueUsd)} icon="trending-up" tone="gold" loading={loading} />
        <AdminStatCard label="Refunded" value={fmtUsd(report?.totalRefundedUsd)} icon="rotate" loading={loading}
          helper={report ? `${fmtPct(report.refundRatePct)} refund rate` : undefined} />
        <AdminStatCard label="New subscriptions" value={report?.newSubscriptions ?? "—"} icon="user-plus" loading={loading} />
        <AdminStatCard label="Canceled" value={report?.canceledSubscriptions ?? "—"} icon="user-minus" loading={loading}
          helper={report ? `${fmtPct(report.cancellationRateApproxPct)} approx. rate` : undefined} />
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <AdminTable
          title="Subscription revenue by plan"
          columns={subColumns}
          rows={report?.revenueBySubscriptionPlan || []}
          loading={loading}
          emptyIcon="credit-card"
          emptyMessage="No subscription revenue in this period."
          defaultSort={{ key: "totalUsd", dir: "desc" }}
        />
        <AdminTable
          title="One-time purchase revenue by credit plan"
          columns={creditColumns}
          rows={report?.revenueByCreditPlan || []}
          loading={loading}
          emptyIcon="coin"
          emptyMessage="No one-time purchase revenue in this period."
          defaultSort={{ key: "totalUsd", dir: "desc" }}
        />
      </div>
    </div>
  );
}
